// IntelligenceModule.jsx — OkBot AI+ (Light Theme, 10 funcionalidades integradas)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Brain, Search, Zap, Inbox, Bell, Plus, Trash2, RefreshCw, Send,
  MessageSquare, MessageCircle, HelpCircle, Calendar, FileText, X,
  CheckCircle2, AlertCircle, Sparkles, Loader2, Tag, Mic, MicOff,
  Lock, Eye, EyeOff, Key, Briefcase, ShieldCheck, FileClock, Sunrise,
  Mail, BarChart3, Copy, ExternalLink, ChevronRight, TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// TEMA CLARO — Paleta de colores definida
// ============================================================================
const T = {
  bg:        'bg-slate-50',
  surface:   'bg-white',
  surface2:  'bg-slate-100',
  border:    'border-slate-200',
  borderH:   'border-slate-300',
  text:      'text-slate-900',
  textMuted: 'text-slate-600',
  textDim:   'text-slate-500',
  textFaint: 'text-slate-400',
  // Acento principal: Indigo
  primary:       'bg-indigo-600 hover:bg-indigo-700 text-white',
  primarySoft:   'bg-indigo-50 text-indigo-700 border-indigo-200',
  primaryRing:   'ring-indigo-500/30 border-indigo-500',
  // Acento secundario: Violet
  accent:        'bg-violet-600 hover:bg-violet-700 text-white',
  accentSoft:    'bg-violet-50 text-violet-700 border-violet-200',
  // Semánticos
  success:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning:    'bg-amber-50  text-amber-800  border-amber-200',
  danger:     'bg-rose-50   text-rose-700   border-rose-200',
  info:       'bg-sky-50    text-sky-700    border-sky-200',
  successDot: 'bg-emerald-500',
  warningDot: 'bg-amber-500',
  dangerDot:  'bg-rose-500',
};

const TABS = [
  { id: 'memory',  label: 'Memoria',    icon: Brain,        color: 'violet' },
  { id: 'search',  label: 'Búsqueda',   icon: Search,       color: 'sky' },
  { id: 'agent',   label: 'Agente',     icon: Zap,          color: 'amber' },
  { id: 'voice',   label: 'Voz',        icon: Mic,          color: 'pink' },
  { id: 'inbox',   label: 'Inbox',      icon: Inbox,        color: 'indigo' },
  { id: 'notif',   label: 'Alertas',    icon: Bell,         color: 'rose' },
  { id: 'digest',  label: 'Resumen',    icon: Sunrise,      color: 'orange' },
  { id: 'vault',   label: 'Vault',      icon: ShieldCheck,  color: 'emerald' },
  { id: 'audit',   label: 'Auditoría',  icon: FileClock,    color: 'slate' },
  { id: 'biz',     label: 'Business',   icon: Briefcase,    color: 'teal' },
];

const ICON_MAP = { MessageCircle, MessageSquare, HelpCircle, Bell, Calendar, FileText, Mail, Inbox };

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const IntelligenceModule = ({ user, onModuleChange }) => {
  const userId = user?.id;
  const [activeTab, setActiveTab] = useState('memory');
  const [unreadInbox, setUnreadInbox] = useState(0);
  const [unreadNotif, setUnreadNotif] = useState(0);

  const refreshCounters = useCallback(async () => {
    if (!userId) return;
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/api/intelligence/inbox/${userId}?limit=1`),
        axios.get(`${API}/api/intelligence/notifications/${userId}?limit=1`),
      ]);
      setUnreadInbox(a.data?.unread || 0);
      setUnreadNotif(b.data?.unread || 0);
    } catch {}
  }, [userId]);

  useEffect(() => { refreshCounters(); const t = setInterval(refreshCounters, 30000); return () => clearInterval(t); }, [refreshCounters]);

  return (
    <div className={`h-full flex flex-col ${T.bg} ${T.text}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 sm:px-6 py-3 border-b ${T.border} ${T.surface}`}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold leading-tight">OkBot AI+</h1>
            <p className={`text-[10px] ${T.textDim} uppercase tracking-wider`}>Suite de productividad inteligente</p>
          </div>
        </div>
        <button onClick={refreshCounters}
          className={`p-2 hover:${T.surface2} rounded-lg ${T.textMuted}`} title="Refrescar">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* Tabs */}
      <div className={`flex items-center gap-1 px-2 sm:px-4 py-2 border-b ${T.border} ${T.surface} overflow-x-auto custom-scrollbar`}>
        {TABS.map(t => {
          const Icon = t.icon;
          const badge = t.id === 'inbox' ? unreadInbox : t.id === 'notif' ? unreadNotif : 0;
          const active = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex-shrink-0
                ${active ? `${T.primarySoft} border` : `${T.textMuted} hover:bg-slate-100 border border-transparent`}`}>
              <Icon size={14} />
              {t.label}
              {badge > 0 && (
                <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-rose-500 text-white rounded-full min-w-[18px] h-[18px] px-1">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3 sm:p-5">
        {activeTab === 'memory' && <MemoryTab userId={userId} />}
        {activeTab === 'search' && <SearchTab userId={userId} onModuleChange={onModuleChange} />}
        {activeTab === 'agent'  && <AgentTab  userId={userId} onDone={refreshCounters} />}
        {activeTab === 'voice'  && <VoiceTab  userId={userId} user={user} />}
        {activeTab === 'inbox'  && <InboxTab  userId={userId} />}
        {activeTab === 'notif'  && <NotifTab  userId={userId} onChanged={refreshCounters} />}
        {activeTab === 'digest' && <DigestTab userId={userId} />}
        {activeTab === 'vault'  && <VaultTab  userId={userId} />}
        {activeTab === 'audit'  && <AuditTab  userId={userId} />}
        {activeTab === 'biz'    && <BusinessTab userId={userId} />}
      </div>
    </div>
  );
};

// ============================================================================
// 1. MEMORIA
// ============================================================================
const MEM_TYPES = [
  { id: 'fact',       label: 'Hecho',       cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  { id: 'preference', label: 'Preferencia', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'context',    label: 'Contexto',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'skill',      label: 'Habilidad',   cls: 'bg-violet-50 text-violet-700 border-violet-200' },
];

const Card = ({ children, className = '' }) => (
  <div className={`${T.surface} border ${T.border} rounded-xl shadow-sm ${className}`}>{children}</div>
);

const Btn = ({ children, variant = 'primary', className = '', ...rest }) => {
  const variants = {
    primary: T.primary,
    accent: T.accent,
    soft: `${T.surface2} ${T.text} hover:bg-slate-200 border ${T.border}`,
    ghost: `${T.textMuted} hover:bg-slate-100`,
    danger: 'bg-rose-600 hover:bg-rose-700 text-white',
  };
  return (
    <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
};

const MemoryTab = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [content, setContent] = useState('');
  const [type, setType] = useState('fact');
  const [extractText, setExtractText] = useState('');
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await axios.get(`${API}/api/intelligence/memory/${userId}`);
      setItems(r.data?.items || []);
    } catch { toast.error('Error al cargar memoria'); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!content.trim()) return toast.error('Escribe algo');
    try {
      await axios.post(`${API}/api/intelligence/memory/${userId}`, { content, type, source: 'manual' });
      setContent(''); toast.success('Memoria guardada'); load();
    } catch { toast.error('Error al guardar'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar esta memoria?')) return;
    try { await axios.delete(`${API}/api/intelligence/memory/${userId}/${id}`); load(); }
    catch { toast.error('Error al eliminar'); }
  };

  const extract = async () => {
    if (!extractText.trim()) return toast.error('Pega texto a analizar');
    setExtracting(true);
    try {
      const r = await axios.post(`${API}/api/intelligence/memory/${userId}/extract`, { text: extractText });
      toast.success(`${r.data?.count || 0} memorias extraídas`);
      setExtractText(''); load();
    } catch { toast.error('Error al extraer'); }
    setExtracting(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Card className="p-4">
        <h2 className="text-sm font-semibold mb-1 flex items-center gap-2"><Brain size={16} className="text-violet-600" /> Memoria persistente</h2>
        <p className={`text-xs ${T.textMuted} mb-3`}>Lo que guardes aquí se inyecta en cada conversación con OkBot para personalizar respuestas.</p>
        <textarea value={content} onChange={e => setContent(e.target.value)} rows={2}
          placeholder="Ej: Prefiero respuestas cortas y directas..."
          className={`w-full ${T.surface2} border ${T.border} rounded-lg px-3 py-2 text-sm outline-none focus:${T.primaryRing} focus:ring-2 mb-2`} />
        <div className="flex gap-2 flex-wrap items-center">
          <select value={type} onChange={e => setType(e.target.value)}
            className={`${T.surface} border ${T.border} rounded-lg px-2 py-1.5 text-xs`}>
            {MEM_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <Btn onClick={add} className="ml-auto"><Plus size={13} /> Guardar</Btn>
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Sparkles size={14} className="text-amber-500" /> Auto-extracción IA</h3>
        <p className={`text-xs ${T.textMuted} mb-3`}>Pega texto largo (notas, biografía, transcripciones) y OkBot extrae lo durable.</p>
        <textarea value={extractText} onChange={e => setExtractText(e.target.value)} rows={3}
          placeholder="Pega texto para extraer memorias..."
          className={`w-full ${T.surface2} border ${T.border} rounded-lg px-3 py-2 text-sm outline-none mb-2`} />
        <Btn onClick={extract} disabled={extracting} variant="accent">
          {extracting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Extraer con IA
        </Btn>
      </Card>

      <div>
        <h3 className={`text-sm font-semibold mb-2 ${T.textMuted}`}>Memorias guardadas ({items.length})</h3>
        {items.length === 0 ?
          <p className={`text-xs ${T.textDim} italic`}>Sin memorias aún. Agrega la primera arriba.</p> :
          <div className="space-y-2">
            {items.map(it => {
              const tp = MEM_TYPES.find(t => t.id === it.type) || MEM_TYPES[0];
              return (
                <Card key={it.id} className="p-3 flex items-start gap-3">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${tp.cls} flex-shrink-0`}>{tp.label}</span>
                  <p className={`flex-1 text-sm ${T.text}`}>{it.content}</p>
                  <button onClick={() => del(it.id)} className={`p-1 ${T.textFaint} hover:text-rose-600`}><Trash2 size={14} /></button>
                </Card>
              );
            })}
          </div>}
      </div>
    </div>
  );
};

// ============================================================================
// 2. BÚSQUEDA SEMÁNTICA
// ============================================================================
const SOURCE_META = {
  memory: { label: 'Memoria', cls: 'text-violet-600 bg-violet-50',  icon: Brain },
  notes:  { label: 'Notas',   cls: 'text-sky-600 bg-sky-50',        icon: FileText },
  events: { label: 'Eventos', cls: 'text-emerald-600 bg-emerald-50',icon: Calendar },
  chats:  { label: 'Chats',   cls: 'text-amber-600 bg-amber-50',    icon: MessageSquare },
};

const SearchTab = ({ userId, onModuleChange }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    if (e) e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/intelligence/search/${userId}`, { query: q, limit: 30 });
      setResults(r.data?.results || []);
      setExpanded(r.data?.expanded_terms || []);
    } catch { toast.error('Error en la búsqueda'); }
    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <form onSubmit={search} className="flex gap-2 mb-4">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Busca en tus notas, eventos, chats, memoria..."
          className={`flex-1 ${T.surface} border ${T.border} rounded-lg px-3 py-2 text-sm outline-none focus:${T.primaryRing} focus:ring-2`} />
        <Btn type="submit" disabled={loading}>
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
        </Btn>
      </form>

      {expanded.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5 items-center">
          <Tag size={12} className={T.textDim} />
          <span className={`text-xs ${T.textDim}`}>Términos relacionados:</span>
          {expanded.map((t, i) => <span key={i} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">{t}</span>)}
        </div>
      )}

      {results.length === 0 && !loading && q && <p className={`text-sm ${T.textDim} text-center py-8`}>Sin resultados</p>}

      <div className="space-y-2">
        {results.map((r, i) => {
          const meta = SOURCE_META[r.source] || { label: r.source, cls: 'text-slate-500 bg-slate-100', icon: FileText };
          const Icon = meta.icon;
          return (
            <Card key={`${r.source}-${r.id}-${i}`} className="p-3 hover:shadow-md cursor-pointer transition-shadow"
              onClick={() => { if (onModuleChange && r.source !== 'memory') {
                const map = { notes: 'notes', events: 'calendar', chats: 'chat' };
                if (map[r.source]) onModuleChange(map[r.source]);
              }}}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${meta.cls}`}>
                  <Icon size={11} /> {meta.label}
                </span>
                <span className={`text-xs font-medium ${T.text} truncate`}>{r.title}</span>
              </div>
              <p className={`text-xs ${T.textMuted} line-clamp-2`}>{r.snippet}</p>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// 3. AGENTE
// ============================================================================
const AgentTab = ({ userId, onDone }) => {
  const [goal, setGoal] = useState('');
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await axios.get(`${API}/api/intelligence/agent/runs/${userId}?limit=10`);
      setHistory(r.data?.items || []);
    } catch {}
  }, [userId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const execute = async () => {
    if (!goal.trim()) return toast.error('Define un objetivo');
    setRunning(true); setRun(null);
    try {
      const r = await axios.post(`${API}/api/intelligence/agent/run`, { user_id: userId, goal, max_steps: 5 });
      setRun(r.data);
      toast.success(r.data?.finished ? 'Agente completó el objetivo' : 'Agente terminó');
      setGoal(''); loadHistory(); if (onDone) onDone();
    } catch (e) { toast.error('Error: ' + (e.response?.data?.detail || e.message)); }
    setRunning(false);
  };

  const examples = [
    'Crea un evento mañana 10am "Revisión semanal" 30 min',
    'Crea una nota con 5 puntos para mejorar mi productividad',
    'Recuérdame mañana llamar al dentista',
    'Busca todo lo guardado sobre "proyecto Alpha"',
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <h2 className="text-sm font-semibold mb-1 flex items-center gap-2"><Zap size={16} className="text-amber-600" /> Agente OkBot</h2>
        <p className={`text-xs ${T.textMuted} mb-3`}>Define un objetivo. El agente ejecutará varios pasos: crear eventos, notas, recordatorios, buscar tus datos.</p>
        <textarea value={goal} onChange={e => setGoal(e.target.value)} rows={2}
          placeholder="Ej: Organízame mañana — bloquea agenda, crea recordatorios..."
          className={`w-full ${T.surface} border ${T.border} rounded-lg px-3 py-2 text-sm outline-none mb-2`} />
        <div className="flex items-center gap-2">
          <Btn onClick={execute} disabled={running || !goal.trim()} variant="accent">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {running ? 'Ejecutando...' : 'Ejecutar agente'}
          </Btn>
          <span className={`text-[10px] ${T.textDim}`}>Máx 5 pasos</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {examples.map((ex, i) => (
            <button key={i} onClick={() => setGoal(ex)}
              className={`text-[10px] ${T.surface} hover:bg-slate-100 border ${T.border} rounded px-2 py-1 ${T.textMuted}`}>
              {ex.length > 50 ? ex.slice(0, 50) + '...' : ex}
            </button>
          ))}
        </div>
      </Card>

      {run && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            {run.finished ? <CheckCircle2 size={15} className="text-emerald-600" /> : <AlertCircle size={15} className="text-amber-600" />}
            <h3 className="text-sm font-semibold">Resultado</h3>
          </div>
          {run.summary && <p className={`text-xs ${T.textMuted} mb-3 italic`}>"{run.summary}"</p>}
          <div className="space-y-2">
            {(run.steps || []).map((s, i) => (
              <div key={i} className={`${T.surface2} rounded p-2 text-xs border ${T.border}`}>
                <div className={`flex items-center gap-2 ${T.textMuted}`}>
                  <span className="font-bold text-violet-700">Paso {s.step}</span>
                  {s.tool && <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">{s.tool}</code>}
                  {s.result?.ok ? <CheckCircle2 size={11} className="text-emerald-600" /> : s.error && <AlertCircle size={11} className="text-rose-600" />}
                </div>
                {s.thought && <p className={`${T.textDim} mt-1 text-[11px] italic`}>{s.thought}</p>}
                {s.error && <p className="text-rose-600 mt-1 text-[11px]">{s.error}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {history.length > 0 && (
        <div>
          <h3 className={`text-sm font-semibold mb-2 ${T.textMuted}`}>Historial reciente</h3>
          <div className="space-y-1.5">
            {history.map(h => (
              <Card key={h.id} onClick={() => setRun(h)} className="p-2.5 cursor-pointer hover:shadow-md flex items-center gap-2 transition-shadow">
                {h.finished ? <CheckCircle2 size={13} className="text-emerald-600 flex-shrink-0" /> : <AlertCircle size={13} className="text-amber-600 flex-shrink-0" />}
                <p className={`text-xs ${T.text} truncate flex-1`}>{h.goal}</p>
                <span className={`text-[10px] ${T.textDim}`}>{(h.steps || []).length} pasos</span>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// 4. VOZ CONTINUA (Web Speech API)
// ============================================================================
const VoiceTab = ({ userId, user }) => {
  const [active, setActive] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [conversation, setConversation] = useState([]);
  const [supported, setSupported] = useState(true);
  const [busy, setBusy] = useState(false);
  const recogRef = useRef(null);
  const restartRef = useRef(false);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = 'es-ES';
    r.onresult = (ev) => {
      let interim = '', final = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) final += t; else interim += t;
      }
      setTranscript(interim);
      if (final.trim()) handleUtterance(final.trim());
    };
    r.onerror = (e) => { if (e.error === 'no-speech') return; toast.error('Voz: ' + e.error); };
    r.onend = () => { if (restartRef.current) { try { r.start(); } catch {} } };
    recogRef.current = r;
    return () => { restartRef.current = false; try { r.stop(); } catch {} };
    // eslint-disable-next-line
  }, []);

  const speak = (text) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'es-ES'; u.rate = 1.05; u.pitch = 1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {}
  };

  const handleUtterance = async (text) => {
    setConversation(c => [...c, { role: 'user', content: text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/chat/send`, {
        user_id: userId, message: text, model: 'super-combo', section: 'general'
      });
      const reply = r.data?.response || r.data?.message || 'Sin respuesta';
      setConversation(c => [...c, { role: 'assistant', content: reply }]);
      speak(reply);
    } catch { toast.error('Error en el chat'); }
    setBusy(false);
    setTranscript('');
  };

  const start = () => {
    if (!recogRef.current) return;
    restartRef.current = true; setActive(true);
    try { recogRef.current.start(); } catch {}
  };
  const stop = () => {
    restartRef.current = false; setActive(false);
    try { recogRef.current.stop(); } catch {}
    try { window.speechSynthesis.cancel(); } catch {}
  };

  if (!supported) {
    return (
      <Card className="p-6 max-w-md mx-auto text-center">
        <MicOff size={32} className="mx-auto mb-2 text-rose-500" />
        <p className="text-sm font-semibold">Voz no soportada</p>
        <p className={`text-xs ${T.textMuted} mt-1`}>Tu navegador no soporta Web Speech. Usa Chrome o Edge.</p>
      </Card>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <Card className="p-6 text-center">
        <div className={`relative mx-auto w-24 h-24 rounded-full flex items-center justify-center transition-all
          ${active ? 'bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg shadow-rose-500/30' : 'bg-slate-200'}`}>
          {active && <span className="absolute inset-0 rounded-full bg-rose-500/30 animate-ping" />}
          {active ? <Mic className="w-10 h-10 text-white" /> : <MicOff className="w-10 h-10 text-slate-500" />}
        </div>
        <p className={`mt-4 text-sm font-semibold ${T.text}`}>
          {active ? (busy ? 'OkBot está pensando...' : 'Escuchando...') : 'Conversación por voz pausada'}
        </p>
        <p className={`text-xs ${T.textMuted} mt-1 min-h-[1em]`}>{transcript || (active ? 'Habla en cualquier momento' : 'Pulsa para iniciar')}</p>
        <div className="flex justify-center gap-2 mt-4">
          {!active ?
            <Btn onClick={start} variant="primary"><Mic size={14} /> Iniciar conversación</Btn> :
            <Btn onClick={stop} variant="danger"><MicOff size={14} /> Detener</Btn>}
        </div>
      </Card>

      {conversation.length > 0 && (
        <Card className="p-4">
          <h3 className={`text-xs font-semibold ${T.textMuted} uppercase mb-2`}>Transcripción</h3>
          <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
            {conversation.map((m, i) => (
              <div key={i} className={`p-2 rounded-lg text-sm ${m.role === 'user' ? 'bg-indigo-50 text-indigo-900' : `${T.surface2} ${T.text}`}`}>
                <span className={`text-[10px] uppercase font-bold ${m.role === 'user' ? 'text-indigo-600' : 'text-violet-600'}`}>
                  {m.role === 'user' ? 'Tú' : 'OkBot'}
                </span>
                <p className="mt-0.5">{m.content}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ============================================================================
// 5. INBOX UNIFICADO (incl. Gmail si está conectado)
// ============================================================================
const InboxTab = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/intelligence/inbox/${userId}?limit=100`);
      setItems(r.data?.items || []);
    } catch { toast.error('Error cargando inbox'); }
    setLoading(false);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const SOURCES = [
    { id: 'all',         label: 'Todo' },
    { id: 'gmail',       label: 'Gmail' },
    { id: 'whatsapp',    label: 'WhatsApp' },
    { id: 'support',     label: 'Soporte' },
    { id: 'notification',label: 'Alertas' },
    { id: 'chat',        label: 'Chats' },
  ];

  const filtered = filter === 'all' ? items : items.filter(i => i.source === filter);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-2"><Inbox size={16} /> Inbox Unificado</h2>
        <button onClick={load} className={`p-1.5 hover:${T.surface2} rounded`}><RefreshCw size={14} /></button>
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {SOURCES.map(s => (
          <button key={s.id} onClick={() => setFilter(s.id)}
            className={`text-xs px-2.5 py-1 rounded-full border ${filter === s.id ? T.primarySoft + ' border' : `${T.surface} ${T.textMuted} ${T.border}`}`}>
            {s.label}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? <p className={`text-xs ${T.textDim}`}>Cargando...</p> :
       filtered.length === 0 ? <p className={`text-xs ${T.textDim} italic text-center py-8`}>Sin mensajes en esta categoría</p> :
       <div className="space-y-1.5">
         {filtered.map((it, i) => {
           const Icon = ICON_MAP[it.icon] || Inbox;
           return (
             <Card key={`${it.source}-${it.id}-${i}`}
               onClick={() => { if (it.link) window.open(it.link, it.link.startsWith('http') ? '_blank' : '_self'); }}
               className={`p-3 cursor-pointer transition-all hover:shadow-md flex items-start gap-3
                 ${!it.is_read ? 'border-l-4 border-l-indigo-500' : ''}`}>
               <div className={`p-2 rounded-lg flex-shrink-0 ${it.is_read ? T.surface2 : T.primarySoft + ' border'}`}>
                 <Icon size={14} />
               </div>
               <div className="flex-1 min-w-0">
                 <div className="flex items-center justify-between gap-2">
                   <p className={`text-xs font-semibold ${T.text} truncate`}>{it.sender}</p>
                   <span className={`text-[10px] ${T.textDim} uppercase flex-shrink-0`}>{it.source}</span>
                 </div>
                 <p className={`text-xs ${T.textMuted} line-clamp-2 mt-0.5`}>{it.preview}</p>
               </div>
               {!it.is_read && <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-2" />}
             </Card>
           );
         })}
       </div>}
    </div>
  );
};

// ============================================================================
// 6. NOTIFICACIONES
// ============================================================================
const NotifTab = ({ userId, onChanged }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    if (!userId) return;
    try { const r = await axios.get(`${API}/api/intelligence/notifications/${userId}?limit=50`); setItems(r.data?.items || []); }
    catch { toast.error('Error cargando alertas'); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => { await axios.post(`${API}/api/intelligence/notifications/${userId}/${id}/read`); load(); onChanged?.(); };
  const markAll  = async () => { await axios.post(`${API}/api/intelligence/notifications/${userId}/read-all`); load(); onChanged?.(); toast.success('Todo leído'); };
  const del      = async (id) => { await axios.delete(`${API}/api/intelligence/notifications/${userId}/${id}`); load(); onChanged?.(); };

  const PRIO = { high: 'border-l-rose-500', normal: 'border-l-indigo-500', low: 'border-l-slate-300' };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-2"><Bell size={16} /> Alertas Proactivas</h2>
        <div className="flex items-center gap-1">
          <Btn variant="ghost" onClick={markAll}>Marcar todo</Btn>
          <button onClick={load} className={`p-1.5 hover:${T.surface2} rounded`}><RefreshCw size={14} /></button>
        </div>
      </div>
      {items.length === 0 ? <p className={`text-xs ${T.textDim} italic text-center py-8`}>Sin alertas. OkBot te avisará cuando haya algo importante.</p> :
       <div className="space-y-2">
         {items.map(n => (
           <Card key={n.id} className={`p-3 border-l-4 ${PRIO[n.priority] || PRIO.normal} ${n.status === 'pending' ? 'bg-indigo-50/40' : ''}`}>
             <div className="flex items-start justify-between gap-2">
               <div className="flex-1 min-w-0">
                 <p className={`text-sm font-semibold ${T.text}`}>{n.title}</p>
                 <p className={`text-xs ${T.textMuted} mt-0.5 whitespace-pre-line`}>{n.body}</p>
                 {n.action && <a href={n.action} className="text-[10px] text-indigo-600 hover:underline mt-1 inline-block">Abrir →</a>}
               </div>
               <div className="flex items-center gap-1 flex-shrink-0">
                 {n.status === 'pending' && <button onClick={() => markRead(n.id)} className={`p-1 hover:${T.surface2} rounded ${T.textMuted}`}><CheckCircle2 size={13} /></button>}
                 <button onClick={() => del(n.id)} className={`p-1 hover:${T.surface2} rounded ${T.textFaint} hover:text-rose-600`}><Trash2 size={13} /></button>
               </div>
             </div>
           </Card>
         ))}
       </div>}
    </div>
  );
};

// ============================================================================
// 7. RESUMEN DIARIO
// ============================================================================
const DigestTab = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try { const r = await axios.get(`${API}/api/intelligence/digest/${userId}`); setItems(r.data?.items || []); } catch {}
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try { await axios.post(`${API}/api/intelligence/digest/${userId}/now`); toast.success('Resumen generado'); load(); }
    catch (e) { toast.info(e.response?.data?.detail || 'Ya existe el resumen de hoy'); }
    setGenerating(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Card className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2"><Sunrise size={16} className="text-orange-600" /> Resumen Diario</h2>
            <p className={`text-xs ${T.textMuted} mt-1`}>OkBot prepara tu briefing cada mañana a las 8am UTC. Genera uno ahora si quieres.</p>
          </div>
          <Btn onClick={generate} disabled={generating} variant="accent">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sunrise size={14} />} Generar ahora
          </Btn>
        </div>
      </Card>

      {items.length === 0 ? <p className={`text-xs ${T.textDim} italic text-center py-6`}>Aún no se ha generado ningún resumen</p> :
       <div className="space-y-2">
         {items.map(d => (
           <Card key={d.id} className="p-4">
             <p className="text-sm font-semibold mb-1">{d.title}</p>
             <p className={`text-xs ${T.textMuted} whitespace-pre-line`}>{d.body}</p>
           </Card>
         ))}
       </div>}
    </div>
  );
};

// ============================================================================
// 8. VAULT
// ============================================================================
const VAULT_TYPES = [
  { id: 'password', label: 'Contraseña' },
  { id: 'api_key',  label: 'API Key' },
  { id: 'card',     label: 'Tarjeta' },
  { id: 'note',     label: 'Nota segura' },
  { id: 'totp',     label: 'TOTP/2FA' },
];

const VaultTab = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'password', value: '', tags: '' });
  const [revealed, setRevealed] = useState({}); // {id: value}

  const load = useCallback(async () => {
    if (!userId) return;
    try { const r = await axios.get(`${API}/api/intelligence/vault/${userId}`); setItems(r.data?.items || []); } catch {}
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.value.trim()) return toast.error('Nombre y valor requeridos');
    try {
      await axios.post(`${API}/api/intelligence/vault/${userId}`, {
        name: form.name, type: form.type, value: form.value,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      toast.success('Guardado encriptado');
      setForm({ name: '', type: 'password', value: '', tags: '' }); setShowForm(false); load();
    } catch { toast.error('Error al guardar'); }
  };

  const reveal = async (id) => {
    if (revealed[id]) { setRevealed(r => { const n = { ...r }; delete n[id]; return n; }); return; }
    try {
      const r = await axios.post(`${API}/api/intelligence/vault/${userId}/${id}/reveal`);
      setRevealed(prev => ({ ...prev, [id]: r.data?.value }));
      setTimeout(() => setRevealed(prev => { const n = { ...prev }; delete n[id]; return n; }), 30000);
    } catch { toast.error('No se pudo desencriptar'); }
  };

  const copy = async (id) => {
    try {
      const r = await axios.post(`${API}/api/intelligence/vault/${userId}/${id}/reveal`);
      await navigator.clipboard.writeText(r.data?.value || '');
      toast.success('Copiado al portapapeles');
    } catch { toast.error('Error al copiar'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar permanentemente?')) return;
    await axios.delete(`${API}/api/intelligence/vault/${userId}/${id}`); load();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Card className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-600" /> Vault de credenciales</h2>
            <p className={`text-xs ${T.textMuted} mt-1`}>AES encriptado. El servidor nunca ve los valores en claro.</p>
          </div>
          <Btn variant="primary" onClick={() => setShowForm(s => !s)}>
            <Plus size={14} /> Nuevo
          </Btn>
        </div>

        {showForm && (
          <div className={`mt-3 p-3 ${T.surface} border ${T.border} rounded-lg space-y-2`}>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nombre (ej: GitHub Token)"
              className={`w-full ${T.surface2} border ${T.border} rounded px-2 py-1.5 text-sm`} />
            <div className="flex gap-2">
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className={`${T.surface2} border ${T.border} rounded px-2 py-1.5 text-xs`}>
                {VAULT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="tags, separados, coma"
                className={`flex-1 ${T.surface2} border ${T.border} rounded px-2 py-1.5 text-xs`} />
            </div>
            <textarea value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} rows={2}
              placeholder="Valor secreto (será encriptado)"
              className={`w-full ${T.surface2} border ${T.border} rounded px-2 py-1.5 text-sm font-mono`} />
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Btn>
              <Btn onClick={create}><Lock size={13} /> Encriptar y guardar</Btn>
            </div>
          </div>
        )}
      </Card>

      {items.length === 0 ? <p className={`text-xs ${T.textDim} italic text-center py-6`}>Vault vacío. Agrega tu primera credencial.</p> :
        <div className="space-y-2">
          {items.map(it => {
            const tp = VAULT_TYPES.find(t => t.id === it.type) || { label: it.type };
            return (
              <Card key={it.id} className="p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${T.text} truncate`}>{it.name}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${T.primarySoft} border`}>{tp.label}</span>
                      {(it.tags || []).map(t => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => copy(it.id)} className={`p-1.5 hover:${T.surface2} rounded`} title="Copiar"><Copy size={13} /></button>
                    <button onClick={() => reveal(it.id)} className={`p-1.5 hover:${T.surface2} rounded`} title="Mostrar/Ocultar">
                      {revealed[it.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                    <button onClick={() => del(it.id)} className={`p-1.5 hover:${T.surface2} rounded ${T.textFaint} hover:text-rose-600`}><Trash2 size={13} /></button>
                  </div>
                </div>
                {revealed[it.id] && (
                  <div className={`${T.surface2} border ${T.border} rounded px-2 py-1.5 mt-1 font-mono text-xs ${T.text} break-all`}>
                    {revealed[it.id]}
                    <p className={`text-[10px] ${T.textDim} mt-1`}>Se ocultará en 30s</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>}
    </div>
  );
};

// ============================================================================
// 9. AUDITORÍA
// ============================================================================
const AuditTab = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const url = filter
        ? `${API}/api/intelligence/audit/${userId}?action=${encodeURIComponent(filter)}&limit=200`
        : `${API}/api/intelligence/audit/${userId}?limit=200`;
      const r = await axios.get(url);
      setItems(r.data?.items || []); setTotal(r.data?.total || 0);
    } catch {}
  }, [userId, filter]);
  useEffect(() => { load(); }, [load]);

  const ACTION_COLORS = {
    'vault.': 'text-emerald-700 bg-emerald-50 border-emerald-200',
    'agent.': 'text-amber-700  bg-amber-50  border-amber-200',
    'memory.':'text-violet-700 bg-violet-50 border-violet-200',
  };
  const colorFor = (a) => Object.entries(ACTION_COLORS).find(([k]) => a.startsWith(k))?.[1] || 'text-slate-700 bg-slate-100 border-slate-200';

  return (
    <div className="max-w-3xl mx-auto">
      <Card className="p-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold flex items-center gap-2"><FileClock size={16} /> Auditoría de acciones</h2>
          <span className={`text-xs ${T.textDim}`}>{total} eventos totales</span>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['', 'vault', 'agent', 'memory'].map(f => (
            <button key={f || 'all'} onClick={() => setFilter(f)}
              className={`text-xs px-2.5 py-1 rounded-full border ${filter === f ? T.primarySoft + ' border' : `${T.surface} ${T.textMuted} ${T.border}`}`}>
              {f || 'Todo'}
            </button>
          ))}
        </div>
      </Card>

      {items.length === 0 ? <p className={`text-xs ${T.textDim} italic text-center py-6`}>Sin eventos registrados</p> :
       <div className="space-y-1.5">
         {items.map(it => (
           <Card key={it.id} className="p-2.5 flex items-center gap-2 text-xs">
             <span className={`px-1.5 py-0.5 rounded border font-mono text-[10px] ${colorFor(it.action)}`}>{it.action}</span>
             <span className={`flex-1 ${T.textMuted} truncate`}>{JSON.stringify(it.details || {})}</span>
             <span className={`text-[10px] ${T.textDim}`}>{(it.created_at || '').slice(0, 16).replace('T', ' ')}</span>
           </Card>
         ))}
       </div>}
    </div>
  );
};

// ============================================================================
// 10. BUSINESS ANALYTICS
// ============================================================================
const BusinessTab = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/intelligence/business/${userId}/analytics?days=${days}`);
      setData(r.data);
    } catch { toast.error('Error cargando analytics'); }
    setLoading(false);
  }, [userId, days]);
  useEffect(() => { load(); }, [load]);

  const KPI = ({ icon: Icon, label, value, sublabel, color }) => (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-xs ${T.textMuted}`}>{label}</p>
          <p className={`text-2xl font-bold ${T.text} mt-0.5`}>{value ?? '—'}</p>
          {sublabel && <p className={`text-[10px] ${T.textDim} mt-0.5`}>{sublabel}</p>}
        </div>
        <div className={`p-2 rounded-lg ${color}`}><Icon size={18} /></div>
      </div>
    </Card>
  );

  const k = data?.kpis || {};
  const max = Math.max(1, ...(data?.timeseries || []).map(t => t.conversations));

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <Card className="p-4 bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2"><Briefcase size={16} className="text-teal-700" /> Business Analytics</h2>
          <p className={`text-xs ${T.textMuted}`}>KPIs avanzados del periodo seleccionado</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className={`${T.surface} border ${T.border} rounded-lg px-2 py-1.5 text-xs`}>
          <option value={7}>7 días</option>
          <option value={30}>30 días</option>
          <option value={90}>90 días</option>
        </select>
      </Card>

      {loading && !data ? <p className={`text-xs ${T.textDim}`}>Cargando...</p> :
       <>
         <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
           <KPI icon={MessageSquare} label="Conversaciones" value={k.conversations_total} sublabel={`+${k.conversations_period} en ${days}d`} color={T.primarySoft + ' border'} />
           <KPI icon={MessageCircle} label="Mensajes WhatsApp" value={k.whatsapp_total} sublabel={`+${k.whatsapp_period} en ${days}d`} color="bg-emerald-50 text-emerald-700 border border-emerald-200" />
           <KPI icon={HelpCircle} label="Tickets soporte" value={k.tickets_total} sublabel={`${k.tickets_open} abiertos`} color="bg-rose-50 text-rose-700 border border-rose-200" />
           <KPI icon={FileText} label="Notas" value={k.notes_total} color="bg-sky-50 text-sky-700 border border-sky-200" />
           <KPI icon={Calendar} label="Eventos" value={k.events_total} color="bg-amber-50 text-amber-700 border border-amber-200" />
           <KPI icon={Zap} label="Runs de Agente" value={k.agent_runs} color="bg-violet-50 text-violet-700 border border-violet-200" />
         </div>

         {data?.timeseries && (
           <Card className="p-4">
             <h3 className="text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-1"><TrendingUp size={13} /> Conversaciones — últimos 7 días</h3>
             <div className="flex items-end gap-1.5 h-32">
               {data.timeseries.map((t, i) => (
                 <div key={i} className="flex-1 flex flex-col items-center gap-1">
                   <div className="w-full bg-indigo-500 rounded-t"
                        style={{ height: `${Math.max(4, (t.conversations / max) * 100)}%` }}
                        title={`${t.conversations} conversaciones`} />
                   <span className={`text-[9px] ${T.textDim}`}>{t.date.slice(5)}</span>
                 </div>
               ))}
             </div>
           </Card>
         )}

         {data?.top_tags?.length > 0 && (
           <Card className="p-4">
             <h3 className="text-xs font-semibold uppercase tracking-wide mb-2">Top tags en notas</h3>
             <div className="flex flex-wrap gap-2">
               {data.top_tags.map(t => (
                 <span key={t.tag} className={`text-xs px-2 py-1 rounded-full border ${T.primarySoft}`}>
                   {t.tag} <span className="text-[10px] opacity-70">×{t.count}</span>
                 </span>
               ))}
             </div>
           </Card>
         )}
       </>}
    </div>
  );
};

export default IntelligenceModule;
